import {type ChecklistDto, type UpdateChecklistResponseData} from '@domain/hroc-checklists'
import {UseMutationResult} from '@tanstack/react-query'
import {type AxiosError} from 'axios'
import React, {useEffect} from 'react'

import {
  ChecklistFormMetaProvider,
  SteppedFormProvider,
  useWidgetTranslation,
  type ChecklistConfig
} from '../hooks'
import {
  mapChecklistDtoToChecklistFormValues,
  mapChecklistFormValuesToChecklistDtoData
} from '../mappers'
import type {ChecklistFormValues, SubmitButtonState} from '../types'

import {StepFormRef} from './StepForm'
import {SteppedForm} from './SteppedForm'

type Props = {
  checklist: ChecklistDto
  checklistConfig: ChecklistConfig
  plantId: string
  readOnly?: boolean
  onCancel?: () => void
  updateMutation?: UseMutationResult<UpdateChecklistResponseData, AxiosError, ChecklistDto['data']>
  backToTargetLabel: string
  stepFormRef?: React.RefObject<StepFormRef>
  onSubmitButtonStateChange: (state: SubmitButtonState) => void
}

export const ChecklistForm = ({
  checklist,
  checklistConfig,
  plantId,
  readOnly,
  onCancel,
  updateMutation,
  backToTargetLabel,
  stepFormRef,
  onSubmitButtonStateChange
}: Props) => {
  const {t} = useWidgetTranslation()
  const initialValues = mapChecklistDtoToChecklistFormValues(checklist, t)

  useEffect(() => {
    const isVisible = checklistConfig.showSubmitInHeader && !readOnly
    const isSubmitting = updateMutation?.isPending ?? false
    onSubmitButtonStateChange({isVisible, isSubmitting})
  }, [
    checklistConfig.showSubmitInHeader,
    readOnly,
    updateMutation?.isPending,
    onSubmitButtonStateChange
  ])

  const handleSubmit = (values: ChecklistFormValues) => {
    updateMutation?.mutate(mapChecklistFormValuesToChecklistDtoData(values, checklist.kind))
  }

  return (
    <ChecklistFormMetaProvider value={{plantId}}>
      <SteppedFormProvider
        checklistConfig={checklistConfig}
        initialValues={initialValues}
        onCancel={onCancel}
        onSubmit={handleSubmit}
        isSubmitting={updateMutation?.isPending}
        readOnly={readOnly}
        backToTargetLabel={backToTargetLabel}
        checklistId={checklist.id}
      >
        <SteppedForm stepFormRef={stepFormRef} />
      </SteppedFormProvider>
    </ChecklistFormMetaProvider>
  )
}
